import { useCallback, useState } from "react";
import { AuthState, OktaAuth } from "@okta/okta-auth-js";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";

import { getConfig } from "merchant-portal-frontend/src/config";
import {
	alertsStorageKey,
	breadTokenStorageKey,
	fakeAuthStorageKey,
	merchantChooserDropdownKey,
} from "merchant-portal-frontend/src/global.constants";
import { browserLogs } from "merchant-portal-frontend/src/logger";
import { BreadToken, decodeBreadToken } from "merchant-portal-frontend/src/model/common/auth";
import { removeItem, useLocalStorage } from "merchant-portal-frontend/src/utils/localStorage";
import { routes } from "merchant-portal-pages/routes";

import { exchangeAccessToken } from "./exchangeAccessToken";

export interface LoginOptions {
	token?: string;
	originalUri?: string;
}

export interface RefreshTokenOptions {
	oktaToken?: string;
}

export interface AuthHookProps {
	isAuthenticated: boolean;
	login: (opts?: LoginOptions) => Promise<void>;
	logout: () => Promise<void>;
	refreshBreadToken: (opts?: RefreshTokenOptions) => Promise<string>;
	breadTokenExchange: (selectedMerchantId: string) => Promise<string>;
	breadToken: BreadToken | null;
	authState: AuthState | null;
}

export function useFakeAuth(): AuthHookProps {
	const navigate = useNavigate();
	const [rawAdminToken] = useLocalStorage<string>(fakeAuthStorageKey, "");
	const [rawBreadToken, setRawBreadToken] = useLocalStorage<BreadToken | null>(
		breadTokenStorageKey,
		null,
	);
	const [breadToken, setBreadToken] = useState<BreadToken | null>(() => {
		if (rawAdminToken) {
			return decodeBreadToken(rawAdminToken);
		}
		if (rawBreadToken) {
			return rawBreadToken;
		}
		return null;
	});

	const login = useCallback(
		async (opts?: LoginOptions) => {
			const { token, originalUri } = opts || {};

			removeItem(merchantChooserDropdownKey);
			removeItem(alertsStorageKey);
			const decodedToken = decodeBreadToken(token);
			setBreadToken(decodedToken);
			setRawBreadToken(decodedToken);
			navigate(originalUri || routes.transactions);
		},
		[navigate, setRawBreadToken],
	);

	const logout = useCallback(async () => {
		setBreadToken(null);
		setRawBreadToken(null);
		navigate(routes.login);
	}, [navigate, setRawBreadToken]);

	const refreshBreadToken = useCallback(async () => {
		return breadToken.accessToken;
	}, [breadToken]);

	const breadTokenExchange = useCallback(
		async (selectedMerchantId: string) => {
			try {
				const newBreadToken = await exchangeAccessToken(
					breadToken.accessToken,
					selectedMerchantId,
				);
				if (newBreadToken) {
					setBreadToken(newBreadToken);
					setRawBreadToken(newBreadToken);
					return newBreadToken.accessToken;
				}
			} catch (error) {
				browserLogs.logger.error({
					message: "Error exchanging tokens",
					context: error,
				});
			}
			return "";
		},
		[breadToken, setRawBreadToken],
	);

	const isAuthenticated = !!breadToken;

	return {
		login,
		logout,
		isAuthenticated,
		breadToken,
		authState: null,
		refreshBreadToken,
		breadTokenExchange,
	};
}

export type OktaAuthProps = Pick<
	{ [k in keyof OktaAuth]: OktaAuth[k] },
	"signOut" | "signInWithRedirect" | "clearStorage" | "setOriginalUri"
>;

export interface OktaAuthHookProps {
	oktaAuth: OktaAuthProps;
	authState: AuthState;
}

export function useOkta({ oktaAuth, authState }: OktaAuthHookProps): AuthHookProps {
	const navigate = useNavigate();

	const [rawBreadToken, setRawBreadToken] = useLocalStorage<BreadToken | null>(
		breadTokenStorageKey,
		null,
	);
	const [breadToken, setBreadToken] = useState<BreadToken | null>(rawBreadToken || null);

	const login = useCallback(
		async (opts?: LoginOptions) => {
			const { token, originalUri } = opts || {};

			removeItem(merchantChooserDropdownKey);
			removeItem(alertsStorageKey);
			await oktaAuth.signInWithRedirect({
				responseType: ["token"],
				scopes: [],
				...(token && { sessionToken: token }),
				...(originalUri && { originalUri }),
			});
		},
		[oktaAuth],
	);

	const logout = useCallback(async () => {
		oktaAuth.clearStorage();
		oktaAuth.setOriginalUri("/");
		try {
			await oktaAuth?.signOut({
				postLogoutRedirectUri: `${window.location.origin}${
					routes.logoutCallback
				}?code=${uuidv4()}`,
				clearTokensBeforeRedirect: true,
				revokeAccessToken: true,
				revokeRefreshToken: true,
			});
		} catch (e) {
			browserLogs.logger.error({
				message: "error occurred signing out of Okta session",
				context: e,
			});
		}
		setBreadToken(null);
		setRawBreadToken(null);
		navigate(routes.logoutCallback);
	}, [navigate, oktaAuth, setRawBreadToken]);

	const refreshBreadToken = useCallback(
		async (opts?: RefreshTokenOptions) => {
			let newBreadToken: BreadToken;
			if (!breadToken) {
				let oktaToken = opts?.oktaToken;
				if (!oktaToken) {
					oktaToken = authState?.accessToken?.accessToken;
				}
				if (oktaToken) {
					newBreadToken = await exchangeAccessToken(oktaToken);
				}
			} else {
				newBreadToken = await exchangeAccessToken(breadToken.accessToken);
			}
			if (newBreadToken) {
				flushSync(() => {
					setBreadToken(newBreadToken);
					setRawBreadToken(newBreadToken);
				});
				return newBreadToken.accessToken;
			}
			return "";
		},
		[authState, breadToken, setRawBreadToken],
	);

	const breadTokenExchange = useCallback(
		async (selectedMerchantId: string) => {
			try {
				const newBreadToken = await exchangeAccessToken(
					breadToken.accessToken,
					selectedMerchantId,
				);
				if (newBreadToken) {
					flushSync(() => {
						setBreadToken(newBreadToken);
						setRawBreadToken(newBreadToken);
					});
					return newBreadToken.accessToken;
				}
			} catch (error) {
				browserLogs.logger.error({
					message: "Error exchanging tokens",
					context: error,
				});
			}
			return "";
		},
		[breadToken, setRawBreadToken],
	);

	return {
		login,
		logout,
		refreshBreadToken,
		breadTokenExchange,
		breadToken,
		isAuthenticated: !!breadToken,
		authState,
	};
}

export const shouldUseFakeAuth = () => getConfig("usefakeauth");

export const authHook = shouldUseFakeAuth() ? useFakeAuth : useOkta;
